import React, { useState } from 'react';
import {
  Box,
  Paper,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
  Drawer,
  CircularProgress,
  Alert,
  Snackbar,
  Button,
  TextField,
  InputAdornment,
  Card,
  Chip,
  Grid,
  Stack,
  Tabs,
  Tab,
} from '@mui/material';
import { Delete, Edit, Add, Search, FilterList, ArrowBack, Refresh } from '@mui/icons-material';

import Slidemenu from '../../components/admin/adminSlidemenu.tsx';
import AdminHeader from '../../components/admin/adminHeader.tsx';

import {
  useListUnitsQuery,
  useAddUnitMutation,
  useUpdateUnitMutation,
  useDeleteUnitMutation,
} from '../../features/admin/unitApi.ts';

interface Unit {
  id: number;
  unit_id?: number;
  unit_name: string;
  unit_short_name: string;
  unit_desc?: string | null;
  createddt?: string;
  updateddt?: string | null;
}

const UnitManagement = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchStr, setSearchStr] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [snackbar, setSnackbar] = useState({ 
    open: false, 
    message: '', 
    severity: 'success' as 'success' | 'error' 
  });

  // Form related states
  const [showForm, setShowForm] = useState(false);
  const [editingUnit, setEditingUnit] = useState<Unit | null>(null);
  const [formData, setFormData] = useState({
    unit_name: '',
    unit_short_name: '',
    unit_desc: '',
  });
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});

  // API hooks
  const { 
    data, 
    isLoading, 
    error, 
    isError, 
    refetch 
  } = useListUnitsQuery({});

  const [addUnit, { isLoading: isAdding }] = useAddUnitMutation();
  const [updateUnit, { isLoading: isUpdating }] = useUpdateUnitMutation();
  const [deleteUnit, { isLoading: isDeleting }] = useDeleteUnitMutation();

  // Handle API response structure
  const units = data?.result || [];
  const totalRecords = units.length;

  const isEditing = Boolean(editingUnit);
  const isFormSubmitting = isAdding || isUpdating;

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchStr(event.target.value);
  };

  // Form handling functions
  const resetForm = () => {
    setFormData({
      unit_name: '',
      unit_short_name: '',
      unit_desc: '',
    });
    setFormErrors({});
    setEditingUnit(null);
  };

  const handleAddUnit = () => {
    resetForm();
    setShowForm(true);
  };

  const handleEditUnit = (unit: Unit) => {
    setFormData({
      unit_name: unit.unit_name || '',
      unit_short_name: unit.unit_short_name || '',
      unit_desc: unit.unit_desc || '',
    });
    setEditingUnit(unit);
    setShowForm(true);
  };

  const handleBackToList = () => {
    setShowForm(false);
    resetForm();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.unit_name.trim()) {
      newErrors.unit_name = 'Unit name is required';
    }

    if (!formData.unit_short_name.trim()) {
      newErrors.unit_short_name = 'Unit short name is required';
    }

    setFormErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      let result;
      if (isEditing && editingUnit) {
        const updateData = {
          unit_id: editingUnit.unit_id || editingUnit.id,
          unit_name: formData.unit_name.trim(),
          unit_short_name: formData.unit_short_name.trim(),
          unit_desc: formData.unit_desc.trim() || undefined,
        };
        
        result = await updateUnit(updateData).unwrap();
        
        setSnackbar({
          open: true,
          message: 'Unit updated successfully!',
          severity: 'success'
        });
      } else {
        const addData = {
          unit_name: formData.unit_name.trim(),
          unit_short_name: formData.unit_short_name.trim(),
          unit_desc: formData.unit_desc.trim() || undefined,
        };
        
        result = await addUnit(addData).unwrap();
        
        setSnackbar({
          open: true,
          message: 'Unit added successfully!',
          severity: 'success'
        });
      }
      
      handleBackToList();
      refetch();
    } catch (err: any) {
      let errorMessage = `Error ${isEditing ? 'updating' : 'adding'} unit.`;
      
      if (err?.data && typeof err.data === 'string' && err.data.includes('<!DOCTYPE')) {
        errorMessage = 'Server error: Received HTML instead of JSON response. Check server configuration.';
      } else if (err?.data?.message) {
        errorMessage = err.data.message;
      }
      
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    }
  };

  const handleDelete = async (unitId: number) => {
    if (window.confirm('Are you sure you want to delete this unit?')) {
      try {
        const deleteData = {
          unit_id: unitId
        };
        
        const result = await deleteUnit(deleteData).unwrap();
        
        setSnackbar({
          open: true,
          message: 'Unit deleted successfully!',
          severity: 'success'
        });
        
        refetch();
      } catch (err: any) {
        let errorMessage = 'Error deleting unit.';
        
        if (err?.data && typeof err.data === 'string' && err.data.includes('<!DOCTYPE')) {
          errorMessage = 'Server error: Received HTML instead of JSON response. Check server configuration.';
        } else if (err?.data?.message) {
          errorMessage = err.data.message;
        }
        
        setSnackbar({
          open: true,
          message: errorMessage,
          severity: 'error'
        });
      }
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  // Filter units based on search
  const filteredUnits = units.filter((unit: Unit) => {
    if (!searchStr) return true;
    const search = searchStr.toLowerCase();
    return (
      unit.unit_name?.toLowerCase().includes(search) ||
      unit.unit_short_name?.toLowerCase().includes(search) ||
      unit.unit_desc?.toLowerCase().includes(search)
    );
  });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: '#f1f2f4' }}>
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        <Box sx={{ width: '250px', flexShrink: 0, display: { xs: 'none', md: 'block' } }}>
          <Slidemenu mobileOpen={false} onToggle={handleDrawerToggle} />
        </Box>
        
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { width: 250 },
          }}
        >
          <Slidemenu onToggle={handleDrawerToggle} mobileOpen={false} />
        </Drawer>
        
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <AdminHeader onToggleMenu={handleDrawerToggle} />
          <Box
            sx={{
              flexGrow: 1,
              overflow: 'auto',
              backgroundColor: '#f1f2f4',
            }}
          >
            {!showForm ? (
              // LIST VIEW
              <Box sx={{ maxWidth: 1600, mx: 'auto', p: 3 }}>
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  mb: 2
                }}>
                  <Typography variant="h5" sx={{ fontWeight: 600, color: '#202223' }}>
                    Units
                  </Typography>
                  
                  <Button
                    variant="contained"
                    onClick={handleAddUnit}
                    sx={{
                      bgcolor: '#202223',
                      color: 'white',
                      textTransform: 'none',
                      fontWeight: 600,
                      borderRadius: '8px',
                      px: 2.5,
                      '&:hover': { bgcolor: '#303030' }
                    }}
                  >
                    Add unit
                  </Button>
                </Box>

                <Card sx={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5', overflow: 'hidden' }}>
                  <Box sx={{ borderBottom: '1px solid #e1e3e5' }}>
                    <Tabs 
                      value={tabValue} 
                      onChange={(e, v) => setTabValue(v)}
                      sx={{
                        px: 2,
                        '& .MuiTab-root': {
                          textTransform: 'none',
                          fontWeight: 500,
                          fontSize: '14px',
                          minHeight: 48,
                          color: '#6d7175',
                        },
                        '& .Mui-selected': {
                          color: '#202223',
                          fontWeight: 600,
                        },
                        '& .MuiTabs-indicator': {
                          backgroundColor: '#202223',
                          height: 3,
                        }
                      }}
                    >
                      <Tab label={`All (${totalRecords})`} />
                    </Tabs>
                  </Box>

                  <Box sx={{ p: 2, borderBottom: '1px solid #e1e3e5', display: 'flex', gap: 1 }}>
                    <TextField
                      size="small"
                      placeholder="Search units"
                      value={searchStr}
                      onChange={handleSearch}
                      fullWidth
                      sx={{ 
                        maxWidth: 400,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px',
                          bgcolor: 'white',
                        }
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search fontSize="small" sx={{ color: '#6d7175' }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                    <Button
                      variant="outlined"
                      startIcon={<FilterList />}
                      sx={{
                        textTransform: 'none',
                        borderColor: '#c9cccf',
                        color: '#202223',
                        borderRadius: '8px',
                        fontWeight: 500,
                        '&:hover': {
                          borderColor: '#202223',
                          bgcolor: 'transparent',
                        }
                      }}
                    >
                      Filter
                    </Button>
                  </Box>

                  {isLoading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300, bgcolor: 'white' }}>
                      <CircularProgress size={32} sx={{ color: '#008060' }} />
                    </Box>
                  )}

                  {isError && !isLoading && (
                    <Box sx={{ p: 3, bgcolor: 'white' }}>
                      <Alert severity="error">Failed to load units. Please try again.</Alert>
                    </Box>
                  )}

                  {!isLoading && !isError && filteredUnits.length === 0 && (
                    <Box sx={{ textAlign: 'center', py: 8, bgcolor: 'white' }}>
                      <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontWeight: 500 }}>
                        No units found
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        {searchStr ? `No results for "${searchStr}"` : 'Get started by adding your first unit'}
                      </Typography>
                    </Box>
                  )}

                  {!isLoading && filteredUnits.length > 0 && (
                    <Box sx={{ bgcolor: 'white' }}>
                      {/* Table Header */}
                      <Box sx={{ 
                        display: 'grid', 
                        gridTemplateColumns: '50px 1fr 200px 300px 150px 100px',
                        gap: 2,
                        px: 2,
                        py: 1.5,
                        borderBottom: '1px solid #e1e3e5',
                        bgcolor: '#fafbfb'
                      }}>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                          No
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                          Unit Name
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                          Short Name
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                          Description
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                          Created Date
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase', textAlign: 'right' }}>
                          Actions
                        </Typography>
                      </Box>

                      {/* Table Rows */}
                      {filteredUnits.map((unit: Unit, index: number) => (
                        <Box
                          key={unit.unit_id ?? unit.id}
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: '50px 1fr 200px 300px 150px 100px',
                            gap: 2,
                            px: 2,
                            py: 2,
                            alignItems: 'center',
                            borderBottom: index < filteredUnits.length - 1 ? '1px solid #e1e3e5' : 'none',
                            '&:hover': { bgcolor: '#f9fafb' },
                            transition: 'background-color 0.15s ease',
                          }}
                        >
                          <Typography variant="body2" sx={{ color: '#6d7175' }}>
                            {index + 1}
                          </Typography>

                          <Typography variant="body2" sx={{ fontWeight: 500, color: '#202223' }}>
                            {unit.unit_name || 'N/A'}
                          </Typography>

                          <Chip 
                            label={unit.unit_short_name || 'N/A'} 
                            size="small" 
                            sx={{ 
                              bgcolor: '#e3f2fd', 
                              color: '#1565c0', 
                              fontWeight: 600, 
                              fontSize: '12px',
                              maxWidth: 'fit-content'
                            }} 
                          />

                          <Typography 
                            variant="body2" 
                            color="text.secondary"
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                            title={unit.unit_desc || 'No description'}
                          >
                            {unit.unit_desc || '-'}
                          </Typography>

                          <Typography variant="body2" color="text.secondary">
                            {formatDate(unit.createddt || '')}
                          </Typography>

                          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                            <IconButton
                              size="small"
                              onClick={() => handleEditUnit(unit)}
                              sx={{ 
                                color: '#6d7175',
                                '&:hover': { bgcolor: '#f1f2f4' }
                              }}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(unit.unit_id ?? unit.id)}
                              disabled={isDeleting}
                              sx={{ 
                                color: '#6d7175',
                                '&:hover': { bgcolor: '#f1f2f4' }
                              }}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Card>
              </Box>
            ) : (
              // FORM VIEW
              <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
                <Box sx={{ mb: 3 }}>
                  <Button
                    startIcon={<ArrowBack />}
                    onClick={handleBackToList}
                    sx={{ 
                      color: '#202223',
                      textTransform: 'none',
                      fontWeight: 500,
                      mb: 2,
                      '&:hover': { bgcolor: 'transparent' }
                    }}
                  >
                    Units
                  </Button>
                  <Typography variant="h5" sx={{ fontWeight: 600, color: '#202223' }}>
                    {isEditing ? 'Edit unit' : 'Add unit'}
                  </Typography>
                </Box>

                <form onSubmit={handleSubmit}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={8}>
                      <Stack spacing={2}>
                        <Card sx={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5' }}>
                          <Box sx={{ p: 3 }}>
                            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#202223' }}>
                              Unit details
                            </Typography>
                            <TextField
                              name="unit_name"
                              label="Unit name"
                              value={formData.unit_name}
                              onChange={handleInputChange}
                              error={!!formErrors.unit_name}
                              helperText={formErrors.unit_name}
                              fullWidth
                              required
                              placeholder="e.g., Kilogram"
                              disabled={isFormSubmitting}
                              sx={{ mb: 2 }}
                            />

                            <TextField
                              name="unit_short_name"
                              label="Short name"
                              value={formData.unit_short_name}
                              onChange={handleInputChange}
                              error={!!formErrors.unit_short_name}
                              helperText={formErrors.unit_short_name || 'Maximum 10 characters'}
                              fullWidth
                              required
                              placeholder="e.g., KG"
                              inputProps={{ maxLength: 10 }}
                              disabled={isFormSubmitting}
                            />
                          </Box>
                        </Card>

                        <Card sx={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5' }}>
                          <Box sx={{ p: 3 }}>
                            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#202223' }}>
                              Description
                            </Typography>
                            <TextField
                              name="unit_desc"
                              value={formData.unit_desc}
                              onChange={handleInputChange}
                              fullWidth
                              multiline
                              rows={5}
                              placeholder="Enter unit description"
                              disabled={isFormSubmitting}
                            />
                          </Box>
                        </Card>
                      </Stack>
                    </Grid>
                  </Grid>

                  <Box sx={{ 
                    display: 'flex', 
                    gap: 2, 
                    justifyContent: 'flex-end', 
                    mt: 3,
                    pt: 3,
                    borderTop: '1px solid #e1e3e5'
                  }}>
                    <Button
                      onClick={handleBackToList}
                      disabled={isFormSubmitting}
                      sx={{ 
                        textTransform: 'none', 
                        fontWeight: 600,
                        color: '#202223',
                        borderRadius: '8px',
                        px: 2.5
                      }}
                    >
                      Discard
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={isFormSubmitting || !formData.unit_name.trim() || !formData.unit_short_name.trim()}
                      sx={{
                        bgcolor: '#008060',
                        textTransform: 'none',
                        fontWeight: 600,
                        borderRadius: '8px',
                        px: 2.5,
                        '&:hover': { bgcolor: '#006e52' }
                      }}
                    >
                      {isFormSubmitting ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CircularProgress size={20} color="inherit" />
                          Saving...
                        </Box>
                      ) : (
                        'Save'
                      )}
                    </Button>
                  </Box>
                </form>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UnitManagement;

